import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, ListTree } from "lucide-react";
import { API } from "@/lib/api";
import ChainState from "./ChainState";
import { useTaal } from "@/contexts/TaalContext";
import { webacties } from "@/lib/agentweb";
import { mailconcepten } from "@/lib/agentmail";
import MailAntwoord from "./MailAntwoord";

/**
 * Wat DEZE agent heeft gedaan, over al zijn gesprekken heen.
 *
 * Een agent-run is een gewoon gesprek, dus zijn spoor was tot 21 augustus 2026
 * alleen per gesprek te vinden en de vraag "wat heeft deze agent deze week
 * gedaan" was niet te stellen.
 *
 * DEZE COMPONENT REKENT NIETS UIT OVER DE KETEN, en dat is met opzet. Elke
 * regel draagt de toestand die de server heeft bepaald tegen de HELE keten van
 * het gesprek waar hij in staat. Wie hier zelf de getoonde regels aan elkaar
 * zou knopen, meet een keten die nooit heeft bestaan: er staan gewone
 * chatregels tussen die deze lijst niet toont. Zo'n berekening zou een breuk
 * melden op elke plek waar zo'n regel tussen twee agent-regels staat, en een
 * echte breuk IN zo'n regel juist niet zien.
 *
 * EN ER STAAT GEEN EERSTE-BREUK. Dat is een uitspraak over EEN keten; deze blik
 * loopt over meerdere. Hem tonen zou suggereren dat deze regels samen een keten
 * vormen.
 */
export default function AgentTrailPanel({ agentId }) {
  const { t } = useTaal();
  const [spoor, setSpoor] = useState(null);
  // W8b: welk concept nu open staat in de mailkaart; een tegelijk.
  const [openConcept, setOpenConcept] = useState(null);
  const [laden, setLaden] = useState(true);
  const [stuk, setStuk] = useState(false);

  useEffect(() => {
    let afgebroken = false;
    setLaden(true);
    setStuk(false);
    setSpoor(null);

    axios
      .get(`${API}/agents/${agentId}/trail`)
      .then(resp => {
        if (!afgebroken) setSpoor(resp.data);
      })
      .catch(() => {
        // Een stille fout is hier het gevaarlijkst: een kapotte verbinding en
        // een agent die niets heeft gedaan zien er dan identiek uit.
        if (!afgebroken) setStuk(true);
      })
      .finally(() => {
        if (!afgebroken) setLaden(false);
      });

    return () => {
      afgebroken = true;
    };
  }, [agentId]);

  if (laden) {
    return (
      <div className="py-4 flex justify-center text-muted-foreground" data-testid="agent-trail-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (stuk) {
    return (
      <p className="text-xs text-destructive py-2" data-testid="agent-trail-error">
        {t("Het spoor van deze agent kon niet worden geladen. Er wordt niets over zijn werk beweerd.")}
      </p>
    );
  }

  const regels = spoor?.entries || [];
  const keten = spoor?.chain || {};

  return (
    <section data-testid="agent-trail">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
        <ListTree className="h-3 w-3" />
        {t("Wat hij heeft gedaan")}
      </h3>

      {regels.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="agent-trail-empty">
          {t("Nog niets. Runs verschijnen hier zodra deze agent iets heeft gedaan.")}
        </p>
      ) : (
        <>
          <ul className="space-y-2 mb-2">
            {regels.map(regel => (
              <li
                key={regel.id}
                data-testid={`agent-trail-row-${regel.id}`}
                className="text-sm border-l-2 border-border pl-2"
              >
                <p className="truncate">{regel.message_preview || "(no preview)"}</p>
                <p className="text-[11px] text-muted-foreground font-mono">
                  {regel.timestamp} - {regel.final_provider}/{regel.final_model} - {regel.duration_ms}ms
                </p>
                <ChainState state={regel.chain} />
                {webacties(regel.steps).length > 0 && (
                  /* W8a: elke webactie van deze run, met adres en uitkomst. */
                  <ul className="mt-1 space-y-0.5" data-testid={`agent-trail-web-${regel.id}`}>
                    {webacties(regel.steps).map((w, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground break-all">
                        {t("Webpagina gelezen: {url} ({uitkomst})", { url: w.url, uitkomst: w.outcome })}
                      </li>
                    ))}
                  </ul>
                )}
                {mailconcepten(regel.steps).length > 0 && (
                  /* W8b: elk concept dat deze run opstelde. Er is niets verstuurd;
                     de knop opent de bestaande mailkaart, en daar verstuur je zelf. */
                  <ul className="mt-1 space-y-1" data-testid={`agent-trail-mail-${regel.id}`}>
                    {mailconcepten(regel.steps).map((c) => (
                      <li key={c.id} className="text-[11px] text-muted-foreground">
                        <p className="break-words">{t("Mailconcept aan {aan}: {onderwerp}", { aan: c.aan, onderwerp: c.onderwerp })}</p>
                        <p>{t("Er is niets verstuurd. Lees het concept na en verstuur het zelf, of niet.")}</p>
                        {openConcept === c.id ? (
                          <div className="mt-1"><MailAntwoord bestaand={c} afzender={c.aan} onderwerp={c.onderwerp} /></div>
                        ) : (
                          <button type="button" className="underline text-[hsl(var(--accent))]"
                                  data-testid={`agent-trail-mail-open-${c.id}`} onClick={() => setOpenConcept(c.id)}>
                            {t("Bekijken en zelf versturen")}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-muted-foreground" data-testid="agent-trail-summary">
            {t("{goed} geverifieerd, {kapot} gebroken, {los} zonder keten.", { goed: keten.verified || 0, kapot: keten.broken || 0, los: keten.unchained || 0 })}
            {/* Glasronde 0.9.189 vondst 8: de server stuurt een vaste Engelse zin
                (`routers/agents.py`); de betekenis staat vast, dus het scherm zegt hem zelf. */}
            {keten.note ? <>{" "}{t("Geteld tegen de volledige keten van elk gesprek, niet tegen deze selectie.")}</> : null}
          </p>
        </>
      )}
    </section>
  );
}
